import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

import {
  ACTION_COOLDOWN_MS,
  IS_LOCALHOST,
  VERIFYING_DELAY_MS,
} from "../constants/payment";
import {
  getPaymentDeepLink,
  getPaymentOptionClass,
  getPaymentOptionSlug,
  normalizeMockOrderData,
  normalizeOrderPaymentOptions,
} from "../markets/shared/paymentOptions";
import { isTerminalStatus, ORDER_STATUS } from "../markets/shared/orderStatus";
import {
  buildManualTransferItems,
  findUpiOption,
  getUpiParamsFromOption,
} from "../markets/inr/upi";
import {
  buildKrwBankDetailRows,
  findKrwBankTransferOption,
  isKrwBankTransferDetailsOption,
} from "../markets/krw/bankTransfer";
import {
  fetchLocalOrderData,
  fetchOrderData,
  getGuidFromUrl,
  getPrefetchedOrder,
  submitUtr,
} from "../services/orderApi";
import { isValidUtr } from "../utils/utr";
import { generateQrDataUrl } from "../utils/qr";
import {
  capturePaymentConfirmationSubmitted,
  capturePaymentMethodAttempted,
  capturePaymentPageLoaded,
  capturePaymentStatusObserved,
  identifyOrder,
} from "../analytics/paymentEvents";

import { useOrderStatus } from "./useOrderStatus";
import { useOrderTimer } from "./useOrderTimer";

export function usePaymentPage({ t, fieldTranslations }) {
  const isLoading = ref(true);
  const errorMessage = ref("");
  const orderData = ref(null);
  const popupState = ref("none");
  const qrLoaded = ref(false);
  const isManualTransferOpen = ref(false);
  const utrInput = ref("");
  const upiAmountOverride = ref(undefined);
  const isMockMode = ref(false);
  const copiedState = reactive({});

  const actionCooldownRemaining = ref(0);
  const actionError = ref("");

  // Last payment method the user actively selected, carried into the
  // confirmation event. Null until a real attempt happens.
  const selectedPaymentMethod = ref(null);
  // Terminal statuses already reported this page lifecycle, to avoid
  // sending the same payment_status_observed twice.
  const observedStatuses = new Set();

  let verifyingTimeout = null;
  let cooldownInterval = null;
  let scrollPosition = 0;

  const {
    timerValue,
    timerVisible,
    timerWarning,
    startTimer,
    clearTimer,
    hideTimer,
  } = useOrderTimer({
    onExpired: () => showExpiredPopup(),
  });

  const {
    orderStatus,
    setOrderStatusFromOrder,
    startStatusPolling,
    stopStatusPolling,
  } = useOrderStatus({
    onTerminal: (status) => {
      if (status === ORDER_STATUS.PAID) {
        showPaidPopup();
      } else if (status === ORDER_STATUS.CANCELED) {
        showCanceledPopup();
      }
    },
    onServerError: () => {
      showErrorPopup();
    },
  });

  const PAYMENT_OPTION_PRIORITY = { upi: 0, paytm: 1, phonepe: 2 };

  const paymentOptions = computed(() => {
    const options = orderData.value?.payment_options || [];
    return [...options].sort((a, b) => {
      const pa = PAYMENT_OPTION_PRIORITY[getPaymentOptionSlug(a)] ?? 99;
      const pb = PAYMENT_OPTION_PRIORITY[getPaymentOptionSlug(b)] ?? 99;
      return pa - pb;
    });
  });

  const isTerminal = computed(() => isTerminalStatus(orderStatus.value));

  const upiOnlyOption = computed(() => findUpiOption(paymentOptions.value));

  const krwBankTransferOption = computed(() =>
    findKrwBankTransferOption(paymentOptions.value, orderData.value),
  );

  const qrSourceOption = computed(() => {
    if (isTerminal.value) return null;
    return (
      paymentOptions.value.find((option) => {
        const slug = getPaymentOptionSlug(option);
        const link = getPaymentDeepLink(option);
        if (!link) return false;
        return slug === "upi" || slug === "kakao_pay";
      }) || null
    );
  });

  const showKrwBankDetailsSection = computed(() => {
    if (isTerminal.value) return false;
    if (qrSourceOption.value) return false;
    const opt = krwBankTransferOption.value;
    return Boolean(opt && isKrwBankTransferDetailsOption(opt, orderData.value));
  });

  const krwBankPanelCaption = computed(() => t("qrCaptionBankKrw"));
  const krwBankPanelHint = computed(() => t("qrHintBankKrw"));
  const krwBankDetailRows = computed(() =>
    buildKrwBankDetailRows(krwBankTransferOption.value, orderData.value, t),
  );

  const hasUtrField = computed(() => Boolean(orderData.value?.requires_utr));

  const currencyCode = computed(
    () => orderData.value?.fiat_money?.currency?.code || "",
  );

  const upiParams = computed(() => getUpiParamsFromOption(upiOnlyOption.value));

  // Funnel dimensions shared across every order event. payment_type is derived
  // from requires_utr; upi_id is the UPI VPA (pa) when the order has one.
  const paymentType = computed(() =>
    orderData.value?.requires_utr ? "utr" : "self_reported",
  );
  const upiId = computed(() => upiParams.value?.pa || "");

  const qrData = computed(() => {
    if (isTerminal.value) return "";

    const opt = qrSourceOption.value;
    if (!opt) return "";

    return getPaymentDeepLink(opt);
  });

  const qrUrl = ref("");
  watch(
    qrData,
    async (data) => {
      qrLoaded.value = false;

      if (!data) {
        qrUrl.value = "";
        return;
      }

      try {
        qrUrl.value = await generateQrDataUrl(data);
      } catch (error) {
        console.log("qr generation failed", error);
        qrUrl.value = "";
      }
    },
    { immediate: true },
  );

  const qrPanelCaption = computed(() =>
    getPaymentOptionSlug(qrSourceOption.value) === "kakao_pay"
      ? t("qrCaptionKakao")
      : t("qrCaption"),
  );

  const qrPanelHint = computed(() =>
    getPaymentOptionSlug(qrSourceOption.value) === "kakao_pay"
      ? t("qrHintKakao")
      : t("qrHint"),
  );

  const qrCopyLabel = computed(() =>
    getPaymentOptionSlug(qrSourceOption.value) === "kakao_pay"
      ? t("kakaoPayLink")
      : t("upiId"),
  );

  const qrCopyValue = computed(() => {
    const opt = qrSourceOption.value;
    if (!opt) return "";
    const slug = getPaymentOptionSlug(opt);
    if (slug === "upi") return upiParams.value?.pa || "";
    if (slug === "kakao_pay") return getPaymentDeepLink(opt);
    return "";
  });

  const manualTransferItems = computed(() =>
    buildManualTransferItems(upiParams.value, fieldTranslations.value, t),
  );

  const finalAmount = computed(
    () => upiAmountOverride.value || orderData.value?.fiat_money?.amount,
  );
  const isInrCurrency = computed(() => currencyCode.value === "INR");

  const showHeaderContent = computed(() => popupState.value === "none");
  const paymentButtonsDisabled = computed(() =>
    ["expired", "canceled", "paid", "processed"].includes(popupState.value),
  );
  const showPaymentButtonsSection = computed(
    () =>
      !isTerminal.value &&
      paymentOptions.value.length > 0 &&
      !showKrwBankDetailsSection.value,
  );
  const showQrSection = computed(() => Boolean(qrSourceOption.value));
  const showManualTransferSection = computed(() =>
    Boolean(
      !isTerminal.value &&
        upiOnlyOption.value &&
        hasUtrField.value &&
        manualTransferItems.value.length,
    ),
  );
  const actionButtonLabel = computed(() =>
    hasUtrField.value ? t("checkPaymentStatus") : t("iPaid"),
  );

  function formatAmount(amount, currency) {
    if (currency === "INR") return `₹${amount}`;
    return `${amount} ${currency}`;
  }

  const displayAmount = computed(() => {
    if (!finalAmount.value) return "";
    return formatAmount(finalAmount.value, currencyCode.value);
  });

  const processedAmountText = computed(() => {
    const fm = orderData.value?.fiat_money;
    if (!fm) return "{{amount}}";
    return formatAmount(fm.amount, fm.currency.code);
  });

  const processedMessage = computed(() =>
    t("paymentProcessedMsg").replace("{{amount}}", processedAmountText.value),
  );
  const paidMessage = computed(() =>
    t("paymentPaidMsg").replace("{{amount}}", processedAmountText.value),
  );
  const canceledMessage = computed(() => t("paymentCanceledMsg"));

  async function copyText(key, value) {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      copiedState[key] = true;
      window.setTimeout(() => {
        copiedState[key] = false;
      }, 1200);
    } catch (error) {
      console.log("clipboard failed", error);
    }
  }

  function clearVerifyingTimeout() {
    if (verifyingTimeout) {
      clearTimeout(verifyingTimeout);
      verifyingTimeout = null;
    }
  }

  function clearCooldownInterval() {
    if (cooldownInterval) {
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    }
  }

  function startActionCooldown() {
    actionCooldownRemaining.value = Math.ceil(ACTION_COOLDOWN_MS / 1000);
    clearCooldownInterval();
    cooldownInterval = window.setInterval(() => {
      actionCooldownRemaining.value -= 1;
      if (actionCooldownRemaining.value <= 0) {
        actionCooldownRemaining.value = 0;
        clearCooldownInterval();
      }
    }, 1000);
  }

  function lockScroll() {
    scrollPosition = window.scrollY || document.documentElement.scrollTop;
    document.body.classList.add("modal-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  function unlockScroll() {
    document.body.classList.remove("modal-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";

    if (scrollPosition) {
      window.scrollTo(0, scrollPosition);
    }
  }

  function observeStatus(status) {
    if (!status || observedStatuses.has(status)) return;
    observedStatuses.add(status);
    capturePaymentStatusObserved({
      order_id: orderData.value?.guid,
      status,
    });
  }

  function showExpiredPopup() {
    clearVerifyingTimeout();
    if (popupState.value === "paid" || popupState.value === "canceled") return;
    popupState.value = "expired";
    observeStatus("expired");
  }

  function showProcessedPopup() {
    clearVerifyingTimeout();
    hideTimer();
    if (popupState.value === "paid" || popupState.value === "canceled") return;
    popupState.value = "processed";
  }

  function showPaidPopup() {
    clearVerifyingTimeout();
    hideTimer();
    popupState.value = "paid";
    observeStatus("paid");
  }

  function showCanceledPopup() {
    clearVerifyingTimeout();
    hideTimer();
    popupState.value = "canceled";
    observeStatus("canceled");
  }

  function showErrorPopup() {
    clearVerifyingTimeout();
    if (popupState.value === "paid" || popupState.value === "canceled") return;
    popupState.value = "error";
  }

  function dismissError() {
    if (popupState.value === "error") popupState.value = "none";
  }

  function openVerifyingPopup() {
    clearVerifyingTimeout();
    if (popupState.value === "paid" || popupState.value === "canceled") return;
    popupState.value = "verifying";
    verifyingTimeout = window.setTimeout(() => {
      showProcessedPopup();
    }, VERIFYING_DELAY_MS);
  }

  // Active selection of a payment method (a deeplink card tap or a QR-based
  // action). Merely rendering the QR or the method list is not an attempt.
  function recordPaymentAttempt(paymentMethod, launchType) {
    selectedPaymentMethod.value = paymentMethod || selectedPaymentMethod.value;
    capturePaymentMethodAttempted({
      order_id: orderData.value?.guid,
      payment_type: paymentType.value,
      upi_id: upiId.value,
      payment_method: paymentMethod,
      launch_type: launchType,
    });
  }

  function handlePaymentOptionSelected(option) {
    if (paymentButtonsDisabled.value) return;
    recordPaymentAttempt(getPaymentOptionSlug(option) || "upi", "deeplink");
  }

  function handleQrAttempt() {
    recordPaymentAttempt(getPaymentOptionSlug(qrSourceOption.value) || "upi", "qr");
  }

  function recordConfirmation() {
    capturePaymentConfirmationSubmitted({
      order_id: orderData.value?.guid,
      payment_type: paymentType.value,
      upi_id: upiId.value,
      payment_method: selectedPaymentMethod.value,
      confirmation_type: orderData.value?.requires_utr ? "utr" : "self_reported",
    });
  }

  async function handleActionButton() {
    if (actionCooldownRemaining.value > 0) return;

    if (hasUtrField.value) {
      const utr = utrInput.value.trim();

      if (!utr) {
        actionError.value = t("utrRequired");
        return;
      }

      if (!isValidUtr(utr)) {
        actionError.value = t("utrInvalid");
        return;
      }

      actionError.value = "";

      recordConfirmation();

      startActionCooldown();
      openVerifyingPopup();

      if (!isMockMode.value) {
        try {
          await submitUtr(getGuidFromUrl(), utr);
        } catch (error) {
          console.log("submit-utr error", error);
          showErrorPopup();
          return;
        }
      }

      showProcessedPopup();
      return;
    }

    recordConfirmation();

    startActionCooldown();
    openVerifyingPopup();
  }

  function handleReturn() {
    if (orderData.value?.return_url) {
      window.location.href = orderData.value.return_url;
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
    }
  }

  async function initializePage() {
    const guid = getGuidFromUrl();

    try {
      if (!guid && IS_LOCALHOST) {
        isMockMode.value = true;
        orderData.value = normalizeMockOrderData(await fetchLocalOrderData());
      } else {
        if (!guid) {
          isLoading.value = false;
          errorMessage.value = t("noGuid");
          return;
        }

        orderData.value = normalizeOrderPaymentOptions(
          await (getPrefetchedOrder() || fetchOrderData(guid)),
        );
      }

      isLoading.value = false;

      // Order data loaded successfully: bind the funnel to this order and send
      // payment_page_loaded before any status/attempt event for the order.
      const guidForAnalytics = orderData.value?.guid;
      identifyOrder(guidForAnalytics);
      capturePaymentPageLoaded({
        order_id: guidForAnalytics,
        payment_type: paymentType.value,
        upi_id: upiId.value,
        currency: currencyCode.value,
        initial_status: orderData.value?.status?.slug,
      });

      if (upiParams.value?.am) {
        upiAmountOverride.value = upiParams.value.am;
      }

      setOrderStatusFromOrder(orderData.value);

      if (orderStatus.value === ORDER_STATUS.PAID) {
        showPaidPopup();
      } else if (orderStatus.value === ORDER_STATUS.CANCELED) {
        showCanceledPopup();
      } else if (orderData.value?.expired_at) {
        startTimer(orderData.value.expired_at);
      }

      if (!isMockMode.value) {
        startStatusPolling();
      }
    } catch (error) {
      isLoading.value = false;
      errorMessage.value = error.message || t("failedLoad");
    }
  }

  watch(
    popupState,
    (state) => {
      if (state === "none") {
        unlockScroll();
        return;
      }

      lockScroll();
    },
    { flush: "post" },
  );

  watch(utrInput, () => {
    if (actionError.value) actionError.value = "";
  });

  onMounted(() => {
    initializePage();
  });

  onBeforeUnmount(() => {
    clearTimer();
    clearVerifyingTimeout();
    clearCooldownInterval();
    stopStatusPolling();
    unlockScroll();
  });

  return {
    actionButtonLabel,
    actionCooldownRemaining,
    actionError,
    canceledMessage,
    copiedState,
    copyText,
    dismissError,
    displayAmount,
    errorMessage,
    getPaymentOptionClass,
    handleActionButton,
    handlePaymentOptionSelected,
    handleQrAttempt,
    handleReturn,
    hasUtrField,
    isInrCurrency,
    isLoading,
    isManualTransferOpen,
    krwBankDetailRows,
    krwBankPanelCaption,
    krwBankPanelHint,
    manualTransferItems,
    orderStatus,
    paidMessage,
    paymentButtonsDisabled,
    paymentOptions,
    popupState,
    processedMessage,
    qrLoaded,
    qrUrl,
    setQrLoaded: (value) => {
      qrLoaded.value = value;
    },
    showHeaderContent,
    showManualTransferSection,
    showPaymentButtonsSection,
    showKrwBankDetailsSection,
    showQrSection,
    qrCopyLabel,
    qrCopyValue,
    qrPanelCaption,
    qrPanelHint,
    timerValue,
    timerVisible,
    timerWarning,
    toggleManualTransfer: () => {
      isManualTransferOpen.value = !isManualTransferOpen.value;
    },
    utrInput,
  };
}
